<template>
  <FetchJson :url="url" @fetched="onFetched">
    <template #default="{ json, loading }">
      <InfoBlock v-if="!loading">
        <template #header>Процесс предоставления услуг</template>
        <template #header-right>
          <div style="width: 380px; height: 85px">
            <DonutChart
              :series="getRates(json)"
              :labels="labels"
              :colors="colors"
              :height="78"
              :legendOffsetX="70"
              :legendOffsetY="-10"
              :tooltipTitleFormatter="() => 'Количество услуг: '"
            />
          </div>
        </template>
        <template #default>
          <BaseTable :columns="columns">
            <template #rows>
              <tr class="table__tr">
                <td class="table__td">Регистрация заявления</td>
                <td class="table__td">
                  {{ formatNumber(json.registrationDuration) }}
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.registrationCompletedCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.registrationCompletedCount"
                    :lowQuality="
                      json.quantity - json.registrationCompletedCount
                    "
                    tooltipHighQuality="Количество услуг, прошедших этап"
                    tooltipLowQuality="Количество услуг, не прошедших этап"
                  />
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.qualitativeRegistrationCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.qualitativeRegistrationCount"
                    :lowQuality="
                      json.quantity - json.qualitativeRegistrationCount
                    "
                    tooltipHighQuality="Количество услуг с качественными данными"
                    tooltipLowQuality="Количество услуг с некачественными данными"
                  />
                </td>
              </tr>
              <tr class="table__tr">
                <td class="table__td">Подготовка результата</td>
                <td class="table__td">
                  {{ formatNumber(json.preparationDuration) }}
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.preparationCompletedCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.preparationCompletedCount"
                    :lowQuality="json.quantity - json.preparationCompletedCount"
                    tooltipHighQuality="Количество услуг, прошедших этап"
                    tooltipLowQuality="Количество услуг, не прошедших этап"
                  />
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.qualitativePreparationCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.qualitativePreparationCount"
                    :lowQuality="
                      json.quantity - json.qualitativePreparationCount
                    "
                    tooltipHighQuality="Количество услуг с качественными данными"
                    tooltipLowQuality="Количество услуг с некачественными данными"
                  />
                </td>
              </tr>
              <tr class="table__tr">
                <td class="table__td">Согласование</td>
                <td class="table__td">
                  {{ formatNumber(json.agreementDuration) }}
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.agreementCompletedCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.agreementCompletedCount"
                    :lowQuality="json.quantity - json.agreementCompletedCount"
                    tooltipHighQuality="Количество услуг, прошедших этап"
                    tooltipLowQuality="Количество услуг, не прошедших этап"
                  />
                </td>
                <td class="table__td">Не рассчитывается</td>
              </tr>
              <tr class="table__tr">
                <td class="table__td">Результат услуги</td>
                <td class="table__td">
                  {{ formatNumber(json.resultDuration) }}
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.resultCompletedCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.resultCompletedCount"
                    :lowQuality="json.quantity - json.resultCompletedCount"
                    tooltipHighQuality="Количество услуг, прошедших этап"
                    tooltipLowQuality="Количество услуг, не прошедших этап"
                  />
                </td>
                <td class="table__td">
                  <PercentProgressIndicator
                    :value="
                      getPercentRounded(
                        json.qualitativeResultCount,
                        json.quantity
                      )
                    "
                    :highQuality="json.qualitativeResultCount"
                    :lowQuality="json.quantity - json.qualitativeResultCount"
                    tooltipHighQuality="Количество услуг с качественными данными"
                    tooltipLowQuality="Количество услуг с некачественными данными"
                  />
                </td>
              </tr>
            </template>
          </BaseTable>
        </template>
      </InfoBlock>
    </template>
  </FetchJson>
</template>

<script>
import { ref, toRefs, computed } from "vue";
import { dateToString } from "@/modules/date";
import { getPercentRounded, formatNumber } from "@/modules/number";
import PercentProgressIndicator from "@/components/common/PercentProgressIndicator";
import DonutChart from "@/components/common/DonutChart";

const labels = ["Высокое качество", "Низкое качество"];
const colors = ["#518CFF", "#FCC8C2"];

export default {
  props: {
    filter: {
      type: Object,
      required: true,
    },
  },
  components: {
    DonutChart,
    PercentProgressIndicator,
  },
  setup(props) {
    const filter = toRefs(props.filter);
    const componentKey = ref(Date.now());
    const url = computed(
      () =>
        `services/service-delivery-process?from=${dateToString(
          filter.from.value
        )}&to=${dateToString(filter.to.value)}&territory=${
          filter.territory.value.alias || ""
        }&serviceType=${filter.serviceType.value.alias || ""}`
    );
    const columns = [
      "Этапы предоставления услуг",
      "Длительность этапа, дней",
      "Услуги, прошедшие этап",
      "Услуги с качественными данными",
    ];

    const getRates = function (tableData) {
      return [
        tableData.qualitativeByAllConditionsCount,
        tableData.qualitativeByNotAllConditionsCount,
      ];
    };

    return {
      componentKey,
      labels,
      colors,
      url,
      columns,
      getRates,
      getPercentRounded,
      formatNumber,
    };
  },
  methods: {
    onFetched() {
      this.componentKey = Date.now(); // Tell Vue to re-create the component.
    },
  },
};
</script>

<style></style>
